import { FC, useCallback } from 'react';
import { Button, ButtonProps } from '@mui/material';
import { Icon } from '@iconify/react';
import { FederatedProvider, startFederatedSignIn } from './federatedAuth';
import { PROVIDER_ICONS, providerLabel } from './providerBranding';

export interface ContinueWithProviderButtonProps {
  /** Which backend provider to start. */
  provider: FederatedProvider;
  /** Optional email used by the backend to resolve the tenant by domain. */
  email?: string;
  /**
   * Relative path to return to after the OAuth round-trip (forwarded to the
   * backend as `next`). Safe-validation happens on the completion page.
   */
  next?: string;
  /** When true, records Terms of Service acceptance (`accept_tos=1`). */
  acceptTos?: boolean;
  /**
   * `primary` = contained, the single promoted CTA for a detected provider.
   * `secondary` = outlined, the default below the "or" divider.
   */
  variant?: 'primary' | 'secondary';
  /** Overrides the default "Continue with {Provider}" label. */
  label?: string;
  fullWidth?: boolean;
  size?: ButtonProps['size'];
  disabled?: boolean;
  /** Test override; defaults to `continue-with-{provider}-button`. */
  dataTestId?: string;
}

export const defaultProviderTestId = (provider: FederatedProvider): string =>
  provider === 'google' || provider === 'google_workspace'
    ? 'continue-with-google-button'
    : `continue-with-${provider}-button`;

const ContinueWithProviderButton: FC<ContinueWithProviderButtonProps> = ({
  provider,
  email,
  next,
  acceptTos = false,
  variant = 'secondary',
  label,
  fullWidth = true,
  size = 'large',
  disabled = false,
  dataTestId,
}) => {
  const handleClick = useCallback((): void => {
    startFederatedSignIn(provider, email, { next, acceptTos });
  }, [provider, email, next, acceptTos]);

  const resolvedLabel = label ?? `Continue with ${providerLabel(provider)}`;

  return (
    <Button
      type="button"
      fullWidth={fullWidth}
      size={size}
      variant={variant === 'primary' ? 'contained' : 'outlined'}
      color="primary"
      disabled={disabled}
      onClick={handleClick}
      startIcon={
        <Icon icon={PROVIDER_ICONS[provider]} width={20} height={20} />
      }
      sx={{ textTransform: 'none' }}
      data-testid={dataTestId ?? defaultProviderTestId(provider)}
    >
      {resolvedLabel}
    </Button>
  );
};

export default ContinueWithProviderButton;
